import { useCallback, useRef } from "react";
import {
  encodeToMp3,
  encodePcmToMp3,
  pcmToWav,
  type EncodedAudio,
  validateAudioConstraints,
} from "@/lib/audio/encoding";
import type { DebugInfo } from "./useDebugMode";

export type PreviewSource = {
  src: string;
  type: string;
  label: "raw" | "mp3";
};

export interface AudioProcessingOptions {
  minDurationSeconds: number;
  maxDurationSeconds: number;
  maxSizeMB: number;
}

export interface ProcessingResult {
  encodedAudio: EncodedAudio;
  previewSources: PreviewSource[];
  debugInfo: DebugInfo;
  rawBlob: Blob; // For debug playback
}

interface UseAudioProcessingOptions {
  constraints: AudioProcessingOptions;
  debugMode: boolean;
  /** Called when processing starts */
  onProcessingStart?: () => void;
  /** Called when processing completes successfully */
  onProcessingComplete?: (result: ProcessingResult) => void;
  /** Called when processing fails */
  onProcessingError?: (error: string) => void;
  /** Reference to check if recording was stopped at time limit */
  stoppedAtTimeLimitRef?: React.RefObject<boolean>;
  /** Reference to preferred MIME type for raw audio */
  preferredMimeTypeRef?: React.RefObject<string | null>;
}

interface UseAudioProcessingReturn {
  /** Process a blob (from MediaRecorder) into MP3 */
  processBlob: (blob: Blob) => Promise<ProcessingResult | null>;
  /** Process PCM data (from Brave/Safari capture) into MP3 */
  processPcm: (pcmData: Float32Array[], sampleRate: number) => Promise<ProcessingResult | null>;
  /** Whether processing is currently in progress */
  isProcessing: boolean;
  /** Clear any created object URLs */
  clearPreviewUrls: () => void;
}

/**
 * Hook to process raw audio data into encoded MP3 with preview sources.
 * Handles both blob-based (Chrome) and PCM-based (Brave/Safari) inputs.
 */
export function useAudioProcessing({
  constraints,
  debugMode,
  onProcessingStart,
  onProcessingComplete,
  onProcessingError,
  stoppedAtTimeLimitRef,
  preferredMimeTypeRef,
}: UseAudioProcessingOptions): UseAudioProcessingReturn {
  const isProcessingRef = useRef(false);
  const previewUrlsRef = useRef<string[]>([]);

  /**
   * Clear all created object URLs to prevent memory leaks.
   */
  const clearPreviewUrls = useCallback(() => {
    previewUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // Ignore errors from already-revoked URLs
      }
    });
    previewUrlsRef.current = [];
  }, []);

  /**
   * Calculate max amplitude from audio data for debug info.
   */
  const calculateMaxAmplitude = (channelData: Float32Array[]): number => {
    let maxAmplitude = 0;
    for (let channel = 0; channel < channelData.length; channel++) {
      const data = channelData[channel];
      for (let i = 0; i < data.length; i++) {
        const abs = Math.abs(data[i]);
        if (abs > maxAmplitude) {
          maxAmplitude = abs;
        }
      }
    }
    return maxAmplitude;
  };

  /**
   * Validate the encoded audio against constraints.
   * Returns null if valid, or an error message if invalid.
   */
  const validateWithTolerance = (
    mp3: EncodedAudio,
    wasStoppedAtTimeLimit: boolean
  ): string | null => {
    const validation = validateAudioConstraints(mp3, {
      minDurationSeconds: constraints.minDurationSeconds,
      maxDurationSeconds: constraints.maxDurationSeconds,
      maxSizeMB: constraints.maxSizeMB,
    });

    if (!validation.ok) {
      // If stopped at time limit and it's a max duration error, check if within tolerance
      const isMaxDurationError = validation.reason.includes("max out at");
      const isWithinTolerance =
        mp3.durationMs <= (constraints.maxDurationSeconds + 2) * 1000;

      if (wasStoppedAtTimeLimit && isMaxDurationError && isWithinTolerance) {
        // Allow recordings stopped at time limit even if slightly over due to encoding
        if (debugMode) {
          console.log(
            `Allowing recording stopped at time limit: ${(mp3.durationMs / 1000).toFixed(2)}s (tolerance: ${constraints.maxDurationSeconds + 2}s)`
          );
        }
        return null;
      }

      return validation.reason;
    }

    return null;
  };

  /**
   * Process a blob from MediaRecorder into MP3.
   */
  const processBlob = useCallback(
    async (blob: Blob): Promise<ProcessingResult | null> => {
      if (isProcessingRef.current) {
        return null;
      }

      isProcessingRef.current = true;
      onProcessingStart?.();

      try {
        // Validate blob before processing
        if (debugMode) {
          console.log("Blob validation:", {
            size: blob.size,
            type: blob.type,
          });
        }

        if (blob.size < 1000) {
          throw new Error(
            `Blob too small: ${blob.size} bytes. Recording may have failed.`
          );
        }

        // Decode to get sample rate and amplitude for logging, and trim if needed
        const AudioContextCtor =
          window.AudioContext ||
          (window as typeof window & { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;

        let decodedSampleRate: number | null = null;
        let maxAmplitude: number | null = null;
        let trimmedBlob: Blob | null = null;

        if (AudioContextCtor) {
          try {
            const decodeContext = new AudioContextCtor();
            const arrayBuffer = await blob.arrayBuffer();
            let audioBuffer = await decodeContext.decodeAudioData(
              arrayBuffer.slice(0)
            );
            decodedSampleRate = audioBuffer.sampleRate;

            // Trim AudioBuffer to exactly maxDurationSeconds
            const maxSamples = Math.floor(
              audioBuffer.sampleRate * constraints.maxDurationSeconds
            );
            if (audioBuffer.length > maxSamples) {
              const trimmedBuffer = decodeContext.createBuffer(
                audioBuffer.numberOfChannels,
                maxSamples,
                audioBuffer.sampleRate
              );
              for (
                let channel = 0;
                channel < audioBuffer.numberOfChannels;
                channel++
              ) {
                const channelData = audioBuffer.getChannelData(channel);
                trimmedBuffer.copyToChannel(
                  channelData.subarray(0, maxSamples),
                  channel
                );
              }
              audioBuffer = trimmedBuffer;

              // Convert trimmed AudioBuffer to PCM data and create WAV blob
              const pcmData: Float32Array[] = [];
              for (
                let channel = 0;
                channel < audioBuffer.numberOfChannels;
                channel++
              ) {
                pcmData.push(audioBuffer.getChannelData(channel));
              }
              trimmedBlob = pcmToWav(
                pcmData,
                audioBuffer.sampleRate,
                audioBuffer.numberOfChannels
              );
            }

            // Calculate max amplitude
            const channelData: Float32Array[] = [];
            for (
              let channel = 0;
              channel < audioBuffer.numberOfChannels;
              channel++
            ) {
              channelData.push(audioBuffer.getChannelData(channel));
            }
            maxAmplitude = calculateMaxAmplitude(channelData);

            await decodeContext.close();
          } catch (e) {
            console.error("Could not decode blob for logging:", e);
            // Don't fail here - encodeToMp3 will handle it
          }
        }

        const mp3 = await encodeToMp3(trimmedBlob || blob);
        const rawType =
          blob.type || preferredMimeTypeRef?.current || "audio/webm";

        // Logging
        if (debugMode) {
          console.log("=== After Decode (Blob) ===");
          console.log("audioBuffer.sampleRate:", decodedSampleRate);
          console.log("duration:", mp3.durationMs / 1000, "seconds");
          console.log("blob size:", mp3.sizeBytes, "bytes");
          console.log("maxAmplitude:", maxAmplitude);
          console.log("blob type:", rawType);
        }

        const debugInfo: DebugInfo = {
          mimeType: preferredMimeTypeRef?.current || rawType,
          sampleRate: mp3.sampleRate,
          blobSize: mp3.sizeBytes,
          duration: mp3.durationMs / 1000,
          maxAmplitude: maxAmplitude,
        };

        // Validate with tolerance for time-limit stops
        const wasStoppedAtTimeLimit = stoppedAtTimeLimitRef?.current ?? false;
        const validationError = validateWithTolerance(mp3, wasStoppedAtTimeLimit);
        if (validationError) {
          if (debugMode) {
            console.error(
              "Validation failed:",
              validationError,
              "Duration:",
              (mp3.durationMs / 1000).toFixed(2) + "s",
              "Stopped at limit:",
              wasStoppedAtTimeLimit
            );
          }
          onProcessingError?.(validationError);
          isProcessingRef.current = false;
          return null;
        }

        // Clear old preview URLs and create new ones
        clearPreviewUrls();
        const mp3Url = URL.createObjectURL(mp3.mp3Blob);
        const rawUrl = URL.createObjectURL(blob);
        previewUrlsRef.current = [mp3Url, rawUrl];

        const result: ProcessingResult = {
          encodedAudio: mp3,
          previewSources: [
            { src: rawUrl, type: rawType, label: "raw" },
            { src: mp3Url, type: "audio/mpeg", label: "mp3" },
          ],
          debugInfo,
          rawBlob: blob,
        };

        onProcessingComplete?.(result);
        isProcessingRef.current = false;
        return result;
      } catch (err) {
        console.error("Error in processBlob:", err);
        console.error("Error details:", {
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
        });
        onProcessingError?.("We couldn't process that post. Please try again.");
        isProcessingRef.current = false;
        return null;
      }
    },
    [
      clearPreviewUrls,
      constraints.maxDurationSeconds,
      constraints.maxSizeMB,
      constraints.minDurationSeconds,
      debugMode,
      onProcessingComplete,
      onProcessingError,
      onProcessingStart,
      preferredMimeTypeRef,
      stoppedAtTimeLimitRef,
    ]
  );

  /**
   * Process PCM data from Brave/Safari capture into MP3.
   */
  const processPcm = useCallback(
    async (
      pcmData: Float32Array[],
      sampleRate: number
    ): Promise<ProcessingResult | null> => {
      if (isProcessingRef.current) {
        return null;
      }

      isProcessingRef.current = true;
      onProcessingStart?.();

      try {
        // Trim PCM data to exactly maxDurationSeconds
        const maxSamples = Math.floor(
          sampleRate * constraints.maxDurationSeconds
        );
        const trimmedPcmData = pcmData.map((channel) =>
          channel.slice(0, maxSamples)
        );

        // Calculate max amplitude from trimmed PCM data
        const maxAmplitude = calculateMaxAmplitude(trimmedPcmData);

        const mp3 = await encodePcmToMp3(trimmedPcmData, sampleRate);

        // Logging
        if (debugMode) {
          console.log("=== After Decode (PCM) ===");
          console.log("audioBuffer.sampleRate:", sampleRate);
          console.log("duration:", mp3.durationMs / 1000, "seconds");
          console.log("blob size:", mp3.sizeBytes, "bytes");
          console.log("maxAmplitude:", maxAmplitude);
          console.log("blob type: audio/mpeg");
        }

        const debugInfo: DebugInfo = {
          mimeType: "audio/mpeg",
          sampleRate: mp3.sampleRate,
          blobSize: mp3.sizeBytes,
          duration: mp3.durationMs / 1000,
          maxAmplitude: maxAmplitude,
        };

        // Validate with tolerance for time-limit stops
        const wasStoppedAtTimeLimit = stoppedAtTimeLimitRef?.current ?? false;
        const validationError = validateWithTolerance(mp3, wasStoppedAtTimeLimit);
        if (validationError) {
          onProcessingError?.(validationError);
          isProcessingRef.current = false;
          return null;
        }

        // Clear old preview URLs and create new ones
        clearPreviewUrls();
        const wavBlob = pcmToWav(
          trimmedPcmData,
          sampleRate,
          trimmedPcmData.length
        );
        const mp3Url = URL.createObjectURL(mp3.mp3Blob);
        const wavUrl = URL.createObjectURL(wavBlob);
        previewUrlsRef.current = [mp3Url, wavUrl];

        const result: ProcessingResult = {
          encodedAudio: mp3,
          previewSources: [
            { src: mp3Url, type: "audio/mpeg", label: "mp3" },
            { src: wavUrl, type: "audio/wav", label: "raw" },
          ],
          debugInfo,
          rawBlob: wavBlob,
        };

        onProcessingComplete?.(result);
        isProcessingRef.current = false;
        return result;
      } catch (err) {
        console.error("Error in processPcm:", err);
        console.error("Error details:", {
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
        });
        onProcessingError?.("We couldn't process that post. Please try again.");
        isProcessingRef.current = false;
        return null;
      }
    },
    [
      clearPreviewUrls,
      constraints.maxDurationSeconds,
      constraints.maxSizeMB,
      constraints.minDurationSeconds,
      debugMode,
      onProcessingComplete,
      onProcessingError,
      onProcessingStart,
      stoppedAtTimeLimitRef,
    ]
  );

  return {
    processBlob,
    processPcm,
    isProcessing: isProcessingRef.current,
    clearPreviewUrls,
  };
}
